import React, { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import '../styles/transactions.css';
import Loader from '../components/Loader';
import MiniDash from '../dashboards/MiniDash';
import ChartInfo from '../components/ChartInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

const Transactions = ({ user }) => {
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [summary, setSummary] = useState({
        totalCredits: 0,
        totalDebits: 0,
        totalTransactions: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [userData, setUserData] = useState(null);
    const [filterType, setFilterType] = useState('all');



    useEffect(() => {
        const fetchTransactions = async () => {
            try {
                const userId = user?.uid;
                if (!userId) {
                    setError('User is not authenticated.');
                    return;
                }

                const userDoc = await getDoc(doc(db, 'greenwtb', userId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUserData(userData);

                    let fetchedTransactions = userData.transactions || [];

                    // Sort transactions by date in descending order (latest first)
                    fetchedTransactions = fetchedTransactions
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .slice(0);
                    // Reverse the order of transactions to show the latest first
                    setTransactions(fetchedTransactions.reverse());
                    // Set initial filtered transactions
                    setFilteredTransactions(fetchedTransactions);

                    const totalCredits = fetchedTransactions.reduce((acc, transaction) => {
                        return transaction.type === 'credit' ? acc + transaction.amount : acc;
                    }, 0);

                    const totalDebits = fetchedTransactions.reduce((acc, transaction) => {
                        return transaction.type === 'debit' ? acc + transaction.amount : acc;
                    }, 0);

                    const totalTransactions = fetchedTransactions.length;

                    setSummary({
                        totalCredits,
                        totalDebits,
                        totalTransactions,
                    });
                }
            } catch (error) {
                setError('Error fetching transactions.');
                console.error('Error fetching transactions: ', error);
            } finally {
                setLoading(false);
            }
        };

        if (user?.uid) {
            fetchTransactions();
        }
    }, [user]);


    const handleCopyAccountNumber = () => {
        if (userData && userData.accountNumber) {
            navigator.clipboard.writeText(userData.accountNumber)
                .then(() => alert('Account number copied to clipboard!'))
                .catch(err => console.error('Failed to copy: ', err));
        }
    };

    const filterTransactions = (type) => {
        setFilterType(type);
        if (type === 'all') {
            setFilteredTransactions(transactions);
        } else {
            const filtered = transactions.filter(transaction => transaction.type === type);
            setFilteredTransactions(filtered);
        }
    };

    if (loading) {
        return <div><Loader /></div>;
    }

    if (error) {
        return <div>{error}</div>;
    }



    return (
        <div className="transactions-container" id='transactions'>
            {/* Two grid to handle mini dash and graph for overview */}
            <div className="flex-container">
                {/* Left box for mini dash and account info */}
                <div className="left-box">
                    <MiniDash />
                    <div className='boxed'>
                        <strong>Account Number: </strong>
                        {userData ? userData.accountNumber : 'Loading...'}
                        <FontAwesomeIcon className="copy-icon" icon={faCopy} onClick={handleCopyAccountNumber} />
                    </div>
                    <div className='flex-container totals'>
                        <div className="right-box">
                            <div className='credits'>Total Credits: ${summary.totalCredits.toFixed(2)}</div>
                        </div>
                        <div className="right-box">
                            <div className='debits'>Total Debits: ${summary.totalDebits.toFixed(2)}</div>
                        </div>
                    </div>
                </div>

                {/* right box with chart information */}
                <div className="right-box">
                    <ChartInfo transactions={transactions} />
                </div>
            </div>



            {/* Recent Transaction and verify transaction */}
            <div className="info-row transactions-header">
                <h2 className="transaction-head">Recent Transactions</h2>
            </div>

            {/* Transaction filter buttons */}
            <div className="filter-buttons">
                <button
                    className={`filter-button ${filterType === 'all' ? 'active' : ''}`}
                    onClick={() => filterTransactions('all')}
                >
                    All
                </button>
                <button
                    className={`filter-button ${filterType === 'credit' ? 'active' : ''}`}
                    onClick={() => filterTransactions('credit')}
                >
                    Credits
                </button>
                <button
                    className={`filter-button ${filterType === 'debit' ? 'active' : ''}`}
                    onClick={() => filterTransactions('debit')}
                >
                    Debits
                </button>
            </div>


            {/* Filtered Transactions */}
            <div className="transactions-list">
                {filteredTransactions.length === 0 ? (
                    <div className="no-transactions">
                        <i className="fas fa-receipt"></i>
                        <p>No transactions available yet.</p>
                    </div>
                ) : (
                    filteredTransactions.map((transaction, index) => (
                        <li
                            key={index}
                            className={`transaction-item ${transaction.type}`}
                            onClick={() => setSelectedTransaction(transaction)}
                        >
                            <div className="transaction-icon">
                                <i className={`fas fa-${transaction.type === 'credit' ? 'circle-plus' : 'circle-minus'}`}></i>
                            </div>
                            <div className="transaction-details">
                                <div className="transaction-description">
                                    {transaction.description}
                                    <div className="transaction-date">
                                        {new Date(transaction.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </div>
                                </div>
                                <div className="transaction-amount">
                                    ${transaction.amount.toFixed(2)}
                                </div>
                            </div>
                        </li>
                    ))
                )}
            </div>


            {/* box for selected transaction */}
            {selectedTransaction && (
                <div className='overlaid'>
                    <div className="details-box show">
                        <div className="details-content">
                            <div className="transaction-description-box">
                                {selectedTransaction.description}
                            </div>
                            <h2 className='acc'>Transaction Details</h2>
                            <ul className="transaction-info">
                                <li><strong>Amount:</strong> ${selectedTransaction.amount.toFixed(2)}</li>
                                <li><strong>Type:</strong> {selectedTransaction.type}</li>
                                <li><strong>Account:</strong> {selectedTransaction.account}</li>
                                <li><strong>Beneficiary:</strong> {selectedTransaction.beneficiary}</li>
                                <li><strong>Date:</strong> {new Date(selectedTransaction.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</li>
                            </ul>
                            <div className="button-container">
                                <span
                                    className="closed"
                                    onClick={() => {
                                        window.location.href = "mailto:support@skyhighntb.com";
                                    }}
                                >
                                    Contact Support
                                </span>


                                <span
                                    className="contact-support-button"
                                    onClick={() => setSelectedTransaction(null)}
                                >
                                    &times; Close</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default Transactions;
